import React, { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import RewardedReviewerIcon from '~/icons/components/RewardedReviewerIcon';
import WhitelabelIcon from '~/icons/components/WhitelabelIcon';
import { ReviewSourceLabels } from '~/pdp/constants/reviewSourceLabels.js';
import { emailAddressIsValid } from '~/shared/utils/validateEmail';
import {
  mobile,
  ShowMoreText,
  ReviewStars,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import {
  FormattedDate,
  FormattedMessage,
  useIntl,
  useRegion,
} from '~/techstyle-shared/react-intl';

import ReviewItemModal from '../ReviewItemModal';

const StyledReviewWrapper = styled.div`
  width: 100%;
  display: flex;
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 32px;
  box-shadow: inset 0 1px 0 0
    ${({ theme }) => theme.colors.recentReviewsWrapperBoxShadow};
  flex-direction: row;
  gap: 5px;

  ${mobile`
    flex-direction: column;
    padding: 16px 0;

    ${({ isModalItem }) =>
      isModalItem &&
      `
      padding: 32px;
    `}
  `}

  ${({ vertical }) =>
    vertical &&
    `
    flex-direction: column;
    padding: 16px 0;
    gap: 0;
  `}

  ${({ isModalItem }) =>
    isModalItem &&
    `
      overflow: auto;
    `}

  ${({ blockStyle }) => blockStyle}
`;

const ReviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 5px;
  width: 250px;
  ${({ vertical }) =>
    vertical &&
    `
    width: 100%;
  `}
`;

const ReviewerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

const ReviewerName = styled.p`
  font-size: 16px;
  font-weight: bold;
  width: min-content;
  ${({ reviewerNameStyle }) => reviewerNameStyle}
`;

const RewardedReviewWrapper = styled.div`
  display: flex;
  margin-left: 8px;
  align-items: center;
`;

const RewardedReviewerIconWrapper = styled.div`
  padding-top: 5px;
`;

const ReviewerIconTitle = styled.span`
  margin-left: 5px;
  font-size: 14px;
`;

const ProductInfoLabel = styled.div`
  display: flex;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkenGray};

  span:last-child {
    margin-left: 4px;
  }

  ${({ vertical }) => vertical && `justify-content: space-between`};
  & + & {
    padding-top: 8px;
  }
  ${({ productInfoStyle }) => productInfoStyle};
`;

const ProductInfoValue = styled.span`
  margin-left: 4px;
`;

const ReviewBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StarsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledStars = styled(ReviewStars)`
  margin-right: 4px;
  font-size: 12px;
  ${({ reviewStarsStyle }) => reviewStarsStyle}
`;

const Date = styled.div`
  display: flex;
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.darkenGray};
  }
  ${({ dateStyle }) => dateStyle}
`;

const ReviewTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  ${mobile`
    font-weight: regular;
  `}
`;

const ReviewContent = styled.span`
  font-size: 14px;
  ${({ contentStyle }) => contentStyle}
`;

const ProductInfoWrapper = styled.div`
  margin-top: 16px;
  ${mobile`display: none;`}
  ${({ productInfoWrapperStyle }) => productInfoWrapperStyle}
`;

const ReviewItemPhotoWrapper = styled.div`
  ${mobile`
    margin-top: 16px;
  `}

  ${({ vertical }) =>
    vertical &&
    `
    margin-top: 16px;
  `}
`;

const ReviewItemPhoto = styled.img`
  width: 100%;
  max-width: 90px;
  display: block;
  cursor: pointer;
`;

const ProductInfo = ({ size, color, style, vertical }) => (
  <>
    {size && (
      <ProductInfoLabel productInfoStyle={style} vertical={vertical}>
        <FormattedMessage
          id="site_pdp.size_purchased"
          defaultMessage="Size Purchased:"
        />
        <ProductInfoValue>{size}</ProductInfoValue>
      </ProductInfoLabel>
    )}
    {color && (
      <ProductInfoLabel productInfoStyle={style} vertical={vertical}>
        <FormattedMessage
          id="site_pdp.color_purchased"
          defaultMessage="Color:"
        />
        <ProductInfoValue>{color}</ProductInfoValue>
      </ProductInfoLabel>
    )}
  </>
);

ProductInfo.propTypes = {
  color: PropTypes.string,
  size: PropTypes.string,
  style: PropTypes.any,
  vertical: PropTypes.bool,
};

const ReviewItem = ({
  review,
  blockStyle,
  vertical,
  reviewerNameStyle,
  productInfoStyle,
  reviewStarsStyle,
  dateStyle,
  contentStyle,
  productInfoWrapperStyle,
  showMoreTextProps,
  reviewStarProps,
  isIncentivizedReviewsEnabled,
  isModalItem,
}) => {
  const {
    reviewerName,
    productSize,
    productColor,
    reviewRating,
    reviewerPhoto,
    reviewSourceLabel,
    reviewTimestamp,
    reviewContent,
    reviewTitle,
  } = review;

  const [isModalOpen, setModalOpen] = useState(false);
  const intl = useIntl();
  const region = useRegion();
  const { desktop } = useBreakpoint();
  const locale = intl.locale;

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const showIncentivizedReview = useMemo(
    () =>
      isIncentivizedReviewsEnabled &&
      [ReviewSourceLabels.SEEDED, ReviewSourceLabels.EMPLOYEE].includes(
        reviewSourceLabel
      ),
    [isIncentivizedReviewsEnabled, reviewSourceLabel]
  );

  const shouldRenderHeaderProductInfo = useMemo(
    () => desktop && !vertical && !isModalItem,
    [desktop, vertical, isModalItem]
  );

  const shouldRenderBodyProductInfo = useMemo(
    () => vertical && !isModalItem,
    [vertical, isModalItem]
  );

  const shouldRenderPhoto = useMemo(
    () => !isModalItem && reviewerPhoto,
    [isModalItem, reviewerPhoto]
  );

  return (
    <StyledReviewWrapper
      blockStyle={blockStyle}
      vertical={vertical}
      isModalItem={isModalItem}
      data-testid="review-item"
    >
      <ReviewHeader vertical={vertical}>
        <ReviewerWrapper>
          <ReviewerName
            reviewerNameStyle={reviewerNameStyle}
            data-testid="review-item-name"
          >
            {emailAddressIsValid(reviewerName) ? (
              <FormattedMessage
                id="site_pdp.reviewer_name_anonymous"
                defaultMessage="Anonymous"
              />
            ) : (
              reviewerName
            )}
          </ReviewerName>
          {showIncentivizedReview && (
            <RewardedReviewWrapper>
              <RewardedReviewerIconWrapper>
                <RewardedReviewerIcon />
              </RewardedReviewerIconWrapper>
              <ReviewerIconTitle>
                <FormattedMessage
                  id="site_pdp.rewarded_reviewer"
                  defaultMessage="Rewarded Reviewer"
                />
              </ReviewerIconTitle>
            </RewardedReviewWrapper>
          )}
        </ReviewerWrapper>
        {shouldRenderHeaderProductInfo && (
          <ProductInfo
            size={productSize}
            color={productColor}
            style={productInfoStyle}
          />
        )}
      </ReviewHeader>
      <ReviewBody>
        <StarsContainer>
          {reviewRating && (
            <StyledStars
              rating={reviewRating}
              reviewStarsStyle={reviewStarsStyle}
              emptyIcon={<WhitelabelIcon iconName="emptyStar" />}
              fullIcon={<WhitelabelIcon iconName="filledStar" />}
              data-testid="review-item-stars-rating"
              {...reviewStarProps}
            />
          )}
          <Date dateStyle={dateStyle} data-testid="review-item-date">
            <FormattedDate
              value={reviewTimestamp}
              day="numeric"
              month={locale === 'en-GB' || region === 'EU' ? 'numeric' : 'long'}
              year="numeric"
            />
          </Date>
        </StarsContainer>
        <ReviewTitle data-testid="review-item-title">{reviewTitle}</ReviewTitle>
        <ReviewContent
          contentStyle={contentStyle}
          data-testid="review-item-content"
        >
          {isModalItem ? (
            <div>{reviewContent}</div>
          ) : (
            <ShowMoreText
              text={reviewContent}
              maxLength={160}
              {...showMoreTextProps}
            />
          )}
        </ReviewContent>
        {shouldRenderPhoto && vertical && (
          <ReviewItemPhotoWrapper vertical={vertical}>
            <ReviewItemPhoto onClick={openModal} src={reviewerPhoto} />
          </ReviewItemPhotoWrapper>
        )}
        {shouldRenderBodyProductInfo && (
          <ProductInfoWrapper
            productInfoWrapperStyle={productInfoWrapperStyle}
            data-testid="review-item-productinfo"
          >
            <ProductInfo
              size={productSize}
              color={productColor}
              style={productInfoStyle}
              vertical
            />
          </ProductInfoWrapper>
        )}
      </ReviewBody>
      {shouldRenderPhoto && !vertical && (
        <ReviewItemPhotoWrapper vertical={vertical}>
          <ReviewItemPhoto onClick={openModal} src={reviewerPhoto} />
        </ReviewItemPhotoWrapper>
      )}
      <ReviewItemModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        review={review}
        reviewerNameStyle={reviewerNameStyle}
        productInfoStyle={productInfoStyle}
        reviewStarsStyle={reviewStarsStyle}
        dateStyle={dateStyle}
        contentStyle={contentStyle}
        productInfoWrapperStyle={productInfoWrapperStyle}
        showMoreTextProps={showMoreTextProps}
        reviewStarProps={reviewStarProps}
        isIncentivizedReviewsEnabled={isIncentivizedReviewsEnabled}
        vertical={vertical}
      />
    </StyledReviewWrapper>
  );
};

ReviewItem.propTypes = {
  blockStyle: PropTypes.any,
  contentStyle: PropTypes.any,
  dateStyle: PropTypes.any,
  isIncentivizedReviewsEnabled: PropTypes.bool,
  isModalItem: PropTypes.bool,
  productInfoStyle: PropTypes.any,
  productInfoWrapperStyle: PropTypes.any,
  review: PropTypes.object.isRequired,
  reviewStarProps: PropTypes.object,
  reviewStarsStyle: PropTypes.any,
  reviewerNameStyle: PropTypes.any,
  showMoreTextProps: PropTypes.any,
  vertical: PropTypes.bool,
};

export default ReviewItem;
